import { type FC, type SVGProps } from 'react';

/**
 * Inline SVG icon set used by the IdentityVerificationCard.
 *
 * Why inline (instead of an icon library): the rest of the form already follows
 * this pattern (WhatsApp / Telegram / chevron icons in basic-information.tsx)
 * and it keeps tree-shaken bundles small. Each icon is monoline-stroke at
 * 1.75 px to pair with the editorial DM Serif Display headings.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const baseProps = (size: number, rest: SVGProps<SVGSVGElement>) => ({
	'width': size,
	'height': size,
	'viewBox': '0 0 24 24',
	'fill': 'none',
	'stroke': 'currentColor',
	'strokeWidth': 1.75,
	'strokeLinecap': 'round' as const,
	'strokeLinejoin': 'round' as const,
	'aria-hidden': true as const,
	'focusable': false,
	...rest,
});

export const CameraIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M5 7h2.5l1.5-2h6l1.5 2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z' />
		<circle
			cx='12'
			cy='13'
			r='3.25'
		/>
	</svg>
);

export const VideoIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<rect
			x='3'
			y='6'
			width='13'
			height='12'
			rx='2'
		/>
		<path d='m16 10 5-2v8l-5-2v-4Z' />
	</svg>
);

/** Document-with-viewfinder icon: viewfinder corner brackets framing a card outline. */
export const ScanCardIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		{/* viewfinder corner brackets */}
		<path d='M3 7V5a2 2 0 0 1 2-2h2' />
		<path d='M21 7V5a2 2 0 0 0-2-2h-2' />
		<path d='M3 17v2a2 2 0 0 0 2 2h2' />
		<path d='M21 17v2a2 2 0 0 1-2 2h-2' />
		{/* card-with-photo glyph inside */}
		<rect
			x='6'
			y='8'
			width='12'
			height='8'
			rx='1.25'
		/>
		<circle
			cx='9'
			cy='11.25'
			r='1'
		/>
		<path d='M12.5 13.5h3.5M12.5 11.5h3.5' />
	</svg>
);

export const UploadIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M21 15v3a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-3' />
		<path d='M17 8 12 3 7 8' />
		<path d='M12 3v13' />
	</svg>
);

/** Replace icon (counter-clockwise loop) — used on the "Replace" success-state action. */
export const ReplaceIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M21 12a9 9 0 1 1-3.2-6.9' />
		<path d='M21 4v5h-5' />
	</svg>
);

/** Retry icon (rotate-ccw) — used on the failed-state action. */
export const RetryIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M3 12a9 9 0 1 0 3.2-6.9' />
		<path d='M3 4v5h5' />
	</svg>
);

export const CheckIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='m5 12 4 4 10-10' />
	</svg>
);

export const SpinnerIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg
		{...baseProps(size, rest)}
		style={{ animation: 'verifySpin 800ms linear infinite', ...(rest.style ?? {}) }}
	>
		<path
			d='M12 3a9 9 0 1 0 9 9'
			opacity='0.95'
		/>
	</svg>
);

/**
 * Decorative face-silhouette placeholder — shown inside the empty selfie thumb so
 * the user can pre-visualise the framing of the shot.
 */
export const FaceSilhouettePlaceholder: FC<IconProps> = ({ size = 44, ...rest }) => (
	<svg
		{...baseProps(size, rest)}
		strokeWidth={1.5}
		opacity={0.55}
	>
		{/* head */}
		<path d='M7.5 10c0-3 2-5.5 4.5-5.5S16.5 7 16.5 10c0 2-1 4-2.25 5' />
		{/* shoulders */}
		<path d='M4.5 20c.5-3 3-5 7.5-5s7 2 7.5 5' />
		{/* chin contour */}
		<path d='M9.5 12.5c0 2 1 3.5 2.5 3.5s2.5-1.5 2.5-3.5' />
	</svg>
);

/** Close (X) icon — used by the camera modal header. */
export const CloseIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M6 6l12 12M18 6 6 18' />
	</svg>
);

/** Camera-switch icon — small camera glyph paired with a circular arrow. */
export const CameraSwitchIcon: FC<IconProps> = ({ size = 18, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<path d='M5 8h2.25l1.25-1.75h7L16.75 8H19a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9' />
		<path d='M3 16a4 4 0 0 0 4 4h1.5' />
		<path d='M7 14v6l-3-3' />
		<circle
			cx='13.5'
			cy='12'
			r='2.5'
		/>
	</svg>
);

/**
 * Decorative face-oval guide used inside the desktop camera modal — drawn at a
 * larger size than `FaceSilhouettePlaceholder` and stroked stronger so it reads
 * clearly over a live video preview. Pure decoration; not interactive.
 */
export const FaceOvalIcon: FC<IconProps> = ({ size = 200, ...rest }) => (
	<svg {...baseProps(size, rest)}>
		<ellipse
			cx='12'
			cy='12'
			rx='6.5'
			ry='8.5'
			strokeWidth={0.5}
		/>
	</svg>
);

/**
 * Decorative voter-card placeholder — a horizontal card outline framed by viewfinder
 * brackets, with a small photo box and ID lines. Tells the user to put a PVC here.
 */
export const VoterCardPlaceholder: FC<IconProps> = ({ size = 44, ...rest }) => (
	<svg
		{...baseProps(size, rest)}
		strokeWidth={1.5}
		opacity={0.55}
	>
		{/* viewfinder corner brackets */}
		<path d='M3 8V5h3' />
		<path d='M21 8V5h-3' />
		<path d='M3 16v3h3' />
		<path d='M21 16v3h-3' />
		{/* card body */}
		<rect
			x='5'
			y='7'
			width='14'
			height='10'
			rx='1.25'
		/>
		{/* photo box */}
		<rect
			x='6.75'
			y='8.75'
			width='3.5'
			height='4.5'
			rx='0.5'
		/>
		{/* id lines */}
		<path d='M12 9.5h5M12 11h4M12 12.5h4M6.75 14.5h10.5' />
	</svg>
);
